import type { ItemMenu, SubItemMenu } from "./navegacao";
import type { UsuarioSessao } from "../types/auth";
import { usuarioDonoPlataforma, usuarioPodeAtenderVendas, usuarioPodeAtenderVendasDtf, usuarioPodeUsarChecklists, usuarioTemAcessoTela } from "../utils/permissoes";
import { rotaLiberadaNasConfiguracoes, type ConfiguracoesSistemaPublicas } from "../utils/configuracoesSistema";

/** Converte uma rota do menu na mesma chave gravada em `telasAcesso` do perfil. */
/* Rotas antigas/atalhos que apontam para a mesma página de uma tela cadastrada
   no Perfil de Acesso. Sem o de-para, a chave derivada da URL não existia no
   perfil e a tela era tratada como bloqueada (botões escondidos e RPCs negados),
   mesmo com o módulo liberado no cadastro. Mesma tabela do back-end em
   backend/src/services/web-rpc-policy.service.js. */
const ALIAS_CHAVE_TELA: Record<string, string> = {
  "vendas/visao-geral": "vendas",
  "compras/visao-geral": "compras",
  "fornecedor/visao-geral": "fornecedor/pedidos",
  estoque: "estoque/saldo",
  "estoque/visao-geral": "estoque/saldo",
  "financeiro/visao-geral": "financeiro/fluxo-caixa-diario-mensal",
  "suporte/visao-geral": "suporte",
  "producao/visao-geral": "producao/lista",
  "estoque/entradas": "estoque/movimentacoes",
  "estoque/saidas": "estoque/movimentacoes",
  "estoque/baixo": "estoque/saldo",
  "estoque-baixo": "estoque/saldo",
  "financeiro/caixa": "financeiro/fluxo-caixa-diario-mensal",
  "financeiro/dre": "financeiro/dre-gerencial-comercial",
  "financeiro/inventario-ajuste-estoque": "estoque/inventario-ajuste-estoque",
  fiscal: "vendas/monitor-fiscal",
};

export function normalizarChaveTela(rota: string): string {
  const semBarra = rota.replace(/^\//, "").replace(/\/+$/, "");
  if (semBarra === "") return "visao-geral";
  return ALIAS_CHAVE_TELA[semBarra] ?? semBarra;
}

function moduloLiberado(modulo: ItemMenu["modulo"], modulosAtivos: string[]): boolean {
  if (!modulo) return true;
  const lista = Array.isArray(modulo) ? modulo : [modulo];
  return lista.some((item) => modulosAtivos.includes(item));
}

function rotaRestritaAoDonoPlataforma(rota: string | undefined): boolean {
  return String(rota || "").startsWith("/configuracoes");
}

export interface SubItemMenuAcessivel extends SubItemMenu {
  bloqueado?: boolean;
}

export interface ItemMenuAcessivel extends ItemMenu {
  bloqueado?: boolean;
  subitens?: SubItemMenuAcessivel[];
}

/**
 * Filtro único de visibilidade do menu: módulos ativos no sistema (configurações
 * globais) continuam escondendo o item, pois o módulo pode nem existir para a
 * organização. Já a falta de acesso do perfil do login (`telasAcesso`) não some
 * mais o item — ele continua visível, apenas marcado como `bloqueado` para a
 * Sidebar desabilitar o clique/navegação mantendo o botão enxergável. Usado
 * tanto pela Sidebar quanto pela lista de telas disponíveis no cadastro de
 * Perfil, para não duplicar a mesma regra em dois lugares.
 */
export function filtrarMenuAcessivel(
  menu: ItemMenu[],
  configuracoes: Pick<ConfiguracoesSistemaPublicas, "modulosAtivos"> &
    Partial<Pick<ConfiguracoesSistemaPublicas, "tefMenu" | "fiscalMenu" | "fiscalIntegracao" | "conciliadorApi">>,
  usuario: UsuarioSessao | null | undefined
): ItemMenuAcessivel[] {
  const donoPlataforma = usuarioDonoPlataforma(usuario);
  return menu
    .map((item): ItemMenuAcessivel | null => {
      if (!item.subitens) {
        if (rotaRestritaAoDonoPlataforma(item.rota) && !donoPlataforma) return null;
        if (!moduloLiberado(item.modulo, configuracoes.modulosAtivos)) return null;
        const bloqueado = !usuarioTemAcessoTela(usuario, normalizarChaveTela(item.rota ?? "/"));
        return { ...item, bloqueado };
      }

      const subitens: SubItemMenuAcessivel[] = item.subitens
        .filter(
          (subitem) =>
            (!rotaRestritaAoDonoPlataforma(subitem.rota) || donoPlataforma) &&
            moduloLiberado(item.modulo, configuracoes.modulosAtivos) &&
            moduloLiberado(
              subitem.modulo ?? item.modulo,
              configuracoes.modulosAtivos
            ) &&
            (subitem.rota !== "/financeiro/conciliacao-bancaria-pix-cnab" || configuracoes.conciliadorApi !== false) &&
            rotaLiberadaNasConfiguracoes(
              subitem.rota,
              configuracoes.tefMenu ?? true,
              configuracoes.fiscalMenu ?? true,
              configuracoes.fiscalIntegracao ?? true
            ) &&
            (
              (subitem.rota !== "/vendas/atendimento" || usuarioPodeAtenderVendas(usuario))
              && (subitem.rota !== "/producao/vendas-dtf" || usuarioPodeAtenderVendasDtf(usuario))
            ) &&
            (subitem.rota !== "/suporte/checklists" || usuarioPodeUsarChecklists(usuario))
        )
        .map((subitem) => ({
          ...subitem,
          bloqueado:
            subitem.rota === "#onboarding"
              ? false
              : ["/vendas/atendimento", "/producao/vendas-dtf"].includes(subitem.rota)
                ? false
                : subitem.rota === "/suporte/checklists"
                  ? false
                : !usuarioTemAcessoTela(usuario, normalizarChaveTela(subitem.rota)),
        }));

      return subitens.length ? { ...item, subitens } : null;
    })
    .filter((item): item is ItemMenuAcessivel => item !== null);
}
